import { useJobsitePrinterOptions } from 'containers/utils';
import React from 'react';
import {
  useCreateCardPrintJobMutation,
  useUpdateWorkerMutation,
  useUploadSingleFileMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError, hasGraphQLErrorMessage, MatchBy } from 'utils/error';
import { uploadCompressedFile, useIsMounted } from 'utils';
import { RequiredField } from 'utils/validation';
import { PrintIcon } from 'components/icons';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { Worker, WorkerTabProps, CardPrintJob } from 'containers/worker/types';
import {
  getFormDefaultValues,
  getFormInputs,
  getWorkerUpdateInput,
  useJobsiteBadgeTemplateOptions,
} from './CreateCardPrintJobModalContent.forms';
import { CreateCardPrintJobFormData } from './types';

export interface CreateCardPrintJobModalContentProps {
  setOpen: () => void;
  onAction: (cardPrintJob: CardPrintJob) => void;
  jobsiteId: string;
  worker: Worker;
  updateWorkerState: WorkerTabProps['updateWorkerState'];
}

const instructions =
  `Please review the following worker data. ` +
  `Any changes made here will be saved to the worker's profile.`;

export function CreateCardPrintJobModalContent(
  props: CreateCardPrintJobModalContentProps,
): React.ReactElement {
  const { worker, jobsiteId, setOpen, onAction, updateWorkerState } = props;
  const { workerId } = worker ?? {};
  const {
    trade: defaultTrade,
    jobTitle: defaultJobTitle,
    jobsiteContractor,
  } = worker?.jobsiteWorkers?.edges.find(
    (edge) => edge.node.jobsiteContractor?.jobsite?.jobsiteId === jobsiteId,
  )?.node ?? {};

  const { defaultJobsitePrinterId } = jobsiteContractor?.jobsite ?? {};

  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const isMounted = useIsMounted();

  /* When a new profile picture is dropped in the modal, we have to upload it first */
  const [uploadFile] = useUploadSingleFileMutation();

  /* If worker data such as trade is updated, we need to update the worker before printing */
  const [updateWorker] = useUpdateWorkerMutation();

  /* When the above are either avoided or executed, we can then create the print job */
  const [createCardPrintJob] = useCreateCardPrintJobMutation();

  /* Combines the above to add a new badge with any new updated data */
  const onSubmit: FormOnSubmit<CreateCardPrintJobFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    try {
      const { profilePictureFile, trade, jobTitle } = data ?? {};
      let profilePictureFileId: string;
      if (profilePictureFile) {
        try {
          profilePictureFileId = await uploadCompressedFile(
            profilePictureFile,
            uploadFile,
          );
        } catch (error) {
          AlertService.alert(
            'danger',
            'Error uploading profile picture',
            getGraphQLError(error),
          );
          return;
        }
      }

      const workerInput = getWorkerUpdateInput(profilePictureFileId);
      if (workerInput) {
        try {
          const updateWorkerResult = await updateWorker({
            variables: { workerId, workerInput },
          });
          const { profilePictureCropped } =
            updateWorkerResult.data.updateWorker;
          updateWorkerState?.({ profilePictureCropped });
        } catch (error) {
          AlertService.alert(
            'danger',
            'Error updating worker data!',
            getGraphQLError(error),
          );
          return;
        }
      }

      try {
        const currentCardPrintJob = await createCardPrintJob({
          variables: {
            input: {
              workerId,
              jobsiteId,
              jobsitePrinterId: data.jobsitePrinter.value,
              cardTemplateName: data.badgeTemplateName.value,
              trade,
              jobTitle,
            },
          },
        });
        onAction(currentCardPrintJob?.data?.createCardPrintJob);
      } catch (error) {
        if (
          hasGraphQLErrorMessage(
            error,
            'Cannot crop profile image for worker',
            MatchBy.startOfMessage,
          )
        ) {
          formApi.setError('profilePictureFile', {
            message: RequiredField`Photo`,
            shouldFocus: true,
          });
        } else {
          AlertService.alert(
            'danger',
            'Error sending card print request',
            getGraphQLError(error),
          );
        }
      }
    } finally {
      if (isMounted()) setIsFetching(false);
    }
  };

  const workerPhotoUrl = worker?.profilePictureCropped?.downloadUrl;
  const badgeTemplateOptions = useJobsiteBadgeTemplateOptions(jobsiteId);
  const { jobsitePrinterOptions } = useJobsitePrinterOptions(jobsiteId);

  const formInputs = React.useMemo(
    () =>
      getFormInputs({
        workerPhotoUrl,
        badgeTemplateOptions,
        jobsitePrinterOptions,
      }),
    [workerPhotoUrl, badgeTemplateOptions, jobsitePrinterOptions],
  );
  const defaultValues = React.useMemo(
    () =>
      getFormDefaultValues({
        defaultTrade,
        defaultJobTitle,
        defaultJobsitePrinterId,
        badgeTemplateOptions,
        jobsitePrinterOptions,
      }),
    [
      defaultTrade,
      defaultJobTitle,
      defaultJobsitePrinterId,
      badgeTemplateOptions,
      jobsitePrinterOptions,
    ],
  );

  return (
    <ModalFormContent
      setOpen={setOpen}
      renderAbove={<p className="odin-mb-4">{instructions}</p>}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Print badge"
      actionIcon={PrintIcon}
      actionButtonWithSpinner={isFetching}
    />
  );
}
